import * as React from "react";
import {
  Alert,
  Button,
  ColumnLayout,
  SegmentedControl,
  SegmentedControlProps,
  Spinner,
  Table,
  Box,
  Link,
  SpaceBetween,
  StatusIndicator,
  Header,
  Icon,
} from '@amzn/awsui-components-react-v3';
import { ItemDetailModel, UserInfoContext, ECCN_STATUS, ITAR_QUESTIONNAIRE_STATUS } from "../../models/item-detail-model";
import { Builder } from "builder-pattern";
import { ItemContainer } from "./ItemContainer"
import { ItemModalComps } from "./ItemModalComps";
import { Question_Status } from "../../models/question-model";

export const REQUIRED_MESSAGE = 'Required';

export namespace ItemTabs {
  export const Detail = (
    props: ItemDetailModel.UI.State
  ) => {

    const adminContainers = (
      <React.Fragment>
        <ItemContainer.Classification state={props.classificationState} />
        <ItemContainer.Approval state={props.approvalState} />
        <ItemContainer.BaselineExemption state={props.baselineExemptionState} />
        <ItemContainer.AdminFiles state={props.adminDocumentState} />
        <ItemModalComps.ConfirmECCNPromotionModal state={props.classificationState.confirmECCNPromotionModalState} />
        <ItemModalComps.ConfirmITARPromotionModal state={props.classificationState.confirmITARPromotionModalState} />
      </React.Fragment>
    )

    return (
      <UserInfoContext.Consumer>
        {value =>
          <div>
            <ColumnLayout columns={1} >
              <ItemContainer.ItemSummary state={props.summaryState} />
              <ItemContainer.Contact state={props.contactState} />
              <ItemContainer.Detail state={props.detailState} />
              {(value.isAdmin || value.isContributor) && props.itemState.value.ItemType === "Software" &&
                <ItemContainer.AmazonDependency state={props.amazonDependencyState} />}
              {(value.isAdmin || value.isContributor) && props.itemState.value.ItemType === "Software" &&
                <ItemContainer.ThirdPartyDependency state={props.thirdPartyDependencyState} />}
              {(value.isAdmin || value.isContributor) &&
                <ItemContainer.RelatedDocument state={props.relatedDocuemntState} />}
              {value.isAdmin && adminContainers}
            </ColumnLayout>
          </div>
        }
      </UserInfoContext.Consumer>
    )
  }

  export const ConditionalQuestionnaireCompletionAlert = (
    props: { state: ItemDetailModel.UI.State, type: 'EAR' | 'ITAR' }
  ) => {
    const { itemState, loading } = props.state;

    if (!!loading.value) {
      return <Alert><Spinner /></Alert>;
    }

    if (props.type === 'ITAR') {
      const status = itemState.value.ITARQuestionnaireStatus;
      if (status === ITAR_QUESTIONNAIRE_STATUS.PENDING_QUESTIONNAIRE_COMPLETION) {
        return <Alert>Please answer all questions to determine the export classification for this item.</Alert>;
      }
      if (status === ITAR_QUESTIONNAIRE_STATUS.ROUTED_TO_EAR) {
        return <Alert type="info">Based on your answers, this item does not appear to be subject to ITAR controls. Please complete the <strong>EAR Questionnaire</strong> tab to determine the applicable export classification.</Alert>;
      }
      if (status === ITAR_QUESTIONNAIRE_STATUS.PENDING_EXPORT_COMPLIANCE_REVIEW) {
        return <Alert type="info">Your answers have been submitted. The Export Compliance team will review and determine the export classification for this item.</Alert>;
      }
      if (status === ITAR_QUESTIONNAIRE_STATUS.ACTIVE) {
        return <Alert type="success">Export classification is active: {itemState.value.ITAR}</Alert>;
      }
      return <></>;
    }

    // EAR
    const status = itemState.value.ECCNStatus;
    if (status === ECCN_STATUS.PENDING_QUESTIONNAIRE_COMPLETION) {
      return <Alert>Please answer all questions to determine the export classification for this item.</Alert>;
    }
    if (status === ECCN_STATUS.PENDING_EXPORT_COMPLIANCE_REVIEW) {
      return <Alert type="info">Your answers have been submitted. The Export Compliance team will review and determine the export classification for this item.</Alert>;
    }
    if (status === ECCN_STATUS.ACTIVE) {
      return <Alert type="success">Export classification is active: {itemState.value.ECCN}</Alert>;
    }
    return <></>;
  }

  export const Questionnaire = (
    props: ItemDetailModel.UI.State
  ) => {

    const accessibilityQuestionnaireFinished = props.accessibilityQuestionnaireState.questionnaire.value.filter(
      q => q.status !== Question_Status.CLOSED
    ).length === 0;
    
    // Determine default tab based on MilitaryUse field
    const getDefaultTab = () => {
      if (props.itemState.value.MilitaryUse === "Yes") {
        return "Military Use Questionnaire";
      }
      return "EAR Questionnaire";
    };

    const [segmentId, setSegmentId] = React.useState<"EAR Questionnaire" | "Military Use Questionnaire" | "Accessibility Questionnaire">(getDefaultTab())
    const [userHasSelectedTab, setUserHasSelectedTab] = React.useState(false);

    // Update selected tab when MilitaryUse changes, unless user has manually selected a tab
    React.useEffect(() => {
      if (!userHasSelectedTab) {
        setSegmentId(getDefaultTab());
      }
    }, [props.itemState.value.MilitaryUse]);

    const earQuestionnaire = (segmentId === "EAR Questionnaire" &&
      <React.Fragment>
        <ItemModalComps.EditQuestionModal state={props.questionnaireState.editModalState} />
        <ItemModalComps.RefreshQuestionModal state={props.questionnaireState.refreshModalState} />
        <ItemContainer.Questionnaire state={props.questionnaireState} />
        <ConditionalQuestionnaireCompletionAlert state={props} type="EAR" />
        <ItemModalComps.SoftwareDependencyModal state={props.softwareDependenciesModalState} />
      </React.Fragment>
    )

    const itarQuestionnaire = (segmentId === "Military Use Questionnaire" &&
      <React.Fragment>
        <ItemModalComps.EditQuestionModal state={props.itarQuestionnaireState.editModalState} />
        <ItemModalComps.RefreshQuestionModal state={props.itarQuestionnaireState.refreshModalState} />
        <ItemContainer.Questionnaire state={props.itarQuestionnaireState} />
        <ConditionalQuestionnaireCompletionAlert state={props} type="ITAR" />
        <ItemModalComps.SoftwareDependencyModal state={props.softwareDependenciesModalState} />
      </React.Fragment>
    )

    const accessibilityQuestionniare = (segmentId === "Accessibility Questionnaire" &&
      <React.Fragment>
        <ItemModalComps.EditAccessibilityQuestionModal state={props.accessibilityQuestionnaireState.editModalState} />
        <ItemModalComps.RefreshAccessibilityQuestionModal state={props.accessibilityQuestionnaireState.refreshModalState} />
        <ItemContainer.Questionnaire state={props.accessibilityQuestionnaireState} />
        {accessibilityQuestionnaireFinished &&
          <Alert type='success'>You have answered all the questions required.</Alert>
        }
      </React.Fragment>
    );

    return (
      <UserInfoContext.Consumer>
        {value =>
          <>
            {/* <Box textAlign="center"> */}
              <SegmentedControl
                className="text-align-center"
                options={[
                  Builder<SegmentedControlProps.Option>().id("EAR Questionnaire").text("EAR Questionnaire").build(),
                  Builder<SegmentedControlProps.Option>().id("Military Use Questionnaire").text("Military Use Questionnaire").build(),
                  Builder<SegmentedControlProps.Option>().id("Accessibility Questionnaire").text("Accessibility Questionnaire").build()
                ]}
                selectedId={segmentId}
                onChange={(e) => { setUserHasSelectedTab(true); setSegmentId(e.detail.selectedId as "EAR Questionnaire" | "Military Use Questionnaire" | "Accessibility Questionnaire"); }}
              />
            {/* </Box> */}
            {earQuestionnaire}
            {itarQuestionnaire}
            {accessibilityQuestionniare}
          </>}
      </UserInfoContext.Consumer>
    )
  }
  export const Supplement = (
    props: ItemDetailModel.UI.State
  ) => {
    return (
      <UserInfoContext.Consumer>
        {value =>
          <ColumnLayout columns={1} >
            {(value.isAdmin || value.isContributor) && <ItemContainer.Supplements state={props.CCATsSupplementState} />}
          </ColumnLayout>
        }
      </UserInfoContext.Consumer>
    )
  }

  export const Components = (
    props: ItemDetailModel.UI.State & { 
      componentItems?: any[];
      onRefreshComponents?: () => void;
      onShowFlashMessage?: (message: any) => void;
    }
  ) => {
    const { componentItems = [], onRefreshComponents, onShowFlashMessage } = props;
    const [loading, setLoading] = React.useState(false);
    const [showAddModal, setShowAddModal] = React.useState(false);

    const MAX_TOTAL_COMPONENTS = 10;
    const canAddComponents = componentItems.length < MAX_TOTAL_COMPONENTS;

    const handleAddSuccess = () => {
      // Show flash message
      if (onShowFlashMessage) {
        onShowFlashMessage({
          type: "success",
          content: (
            <>
              Related Item created successfully. Click the <Icon name="refresh" /> refresh button to see the new Related Item in the table.
            </>
          ),
          dismissible: true,
        });
      }
    };

    const handleRefresh = () => {
      if (onRefreshComponents) {
        setLoading(true);
        onRefreshComponents();
        // Reset loading after a short delay to show feedback
        setTimeout(() => setLoading(false), 500);
      }
    };

    const columnDefinitions = [
      {
        id: "ItemFullName",
        header: "Name",
        cell: (item: any) => (
          <Link href={`#/items/${item.Id}`} external>{item.ItemFullName}</Link>
        ),
        sortingField: "ItemFullName",
        width: 250,
      },
      {
        id: "CodeName",
        header: "Internal Code Name",
        cell: (item: any) => item.CodeName,
        sortingField: "CodeName",
        width: 150,
      },
      {
        id: "ItemType",
        header: "Item Type",
        cell: (item: any) => item.ItemType || "-",
        sortingField: "ItemType",
        width: 150,
      },
      {
        id: "ECCN",
        header: "ECCN",
        cell: (item: any) => item.ECCN || item.ECCNStatus || "Pending",
        sortingField: "ECCN",
        width: 120,
      },
      {
        id: "ECCNStatus",
        header: "ECCN Status",
        cell: (item: any) => {
          const status = item.ECCNStatus || "Pending questionnaire completion";
          if (status === ECCN_STATUS.ACTIVE) {
            return <StatusIndicator type="success">{status}</StatusIndicator>;
          }
          return <StatusIndicator type="pending">{status}</StatusIndicator>;
        },
        sortingField: "ECCNStatus",
        width: 180,
      },
      {
        id: "Pm",
        header: "Product Manager",
        cell: (item: any) => (
          <Link href={`https://phonetool.amazon.com/users/${item.Pm}`} external>
            {item.Pm}
          </Link>
        ),
        sortingField: "Pm",
        width: 150,
      },
      {
        id: "Description",
        header: "Description",
        cell: (item: any) => (
          <Box>{item.Description?.substring(0, 100)}{item.Description?.length > 100 ? '...' : ''}</Box>
        ),
        width: 300,
      },
    ];

    return (
      <UserInfoContext.Consumer>
        {value => (
          <>
            <ItemModalComps.AddComponentsModal
              visible={showAddModal}
              parentItemId={props.itemState.value.Id}
              parentItemData={props.itemState.value}
              existingComponentsCount={componentItems.length}
              onDismiss={() => setShowAddModal(false)}
              onSuccess={handleAddSuccess}
            />
            <Table
              header={
                <Header
                  counter={`(${componentItems.length})`}
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button
                        onClick={handleRefresh}
                        iconName="refresh"
                        loading={loading}
                        disabled={loading}
                      >
                        Refresh
                      </Button>
                      {(value.isAdmin || value.isContributor) && (
                        <Button
                          onClick={() => setShowAddModal(true)}
                          disabled={!canAddComponents}
                          iconName="add-plus"
                        >
                          Add Related Items
                        </Button>
                      )}
                    </SpaceBetween>
                  }
                >
                  Related Items
                </Header>
              }
              columnDefinitions={columnDefinitions}
              items={componentItems}
              loading={loading}
              loadingText="Loading Related Items..."
              empty={
                <Box textAlign="center" color="inherit">
                  <SpaceBetween size="m">
                    <b>No Related Items</b>
                    <Box variant="p" color="inherit">
                      This Service does not have any Related Items linked yet.
                      {(value.isAdmin || value.isContributor) && canAddComponents && (
                        <Box margin={{ top: 's' }}>
                          <Button onClick={() => setShowAddModal(true)} iconName="add-plus">
                            Add Related Items
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </SpaceBetween>
                </Box>
              }
              sortingDisabled={false}
              variant="embedded"
              stickyHeader={true}
            />
          </>
        )}
      </UserInfoContext.Consumer>
    );
  };
}
